import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import QuotationHeaderComponent from './QuotationHeaderComponent';
import RisksComponent from './RisksComponent';
import CoefficientsComponent from '../CoefficientsComponent';
import { Grid, Box, Tooltip, Slide, Chip } from '@mui/material';
import { IconButton, Button, Typography } from '@totalsoft/rocket-ui';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import UpdateIcon from '@mui/icons-material/Update';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { get, sequence } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { QuotationCard, QuotationTitleWrapper, QuotationButton } from '../CascoStyles';

const QuotationGridComponent = ({
  quotationLens,
  validation,
  index,
  colorTheme,
  onSave,
  onRetry,
  onSelect,
  onQuotationLockChange,
  onHeaderPropertyChange,
  onRiskSelectionChange,
  onCoefficientsSelectionChange,
  onAmountChange,
  isDirty,
  saving,
  lockUpdating,
  quotationSelected,
  disabled,
  allowEditForNegotiation,
  allowSelection,
  allowCustomQuotationEdit,
  allowQuotationsLockUpdate,
  allowRetry,
  titleRef,
  showTitleTooltip,
  messageRef,
  showMessageTooltip,
  onEditCustomQuotation,
  allowEmptySelection,
  isLastQuotationModified
}) => {
  const { t } = useTranslation();

  const displayLockArea = allowQuotationsLockUpdate || (quotationLens.lockedYN |> get);

  const cardIconColor = useMemo(() => {
    if (quotationLens.sourceQuotation |> get) return 'info';
    if (quotationLens.sourceQuotationId |> get) return 'success';
    return 'primary';
  }, [quotationLens]);

  return (
    <Slide direction='right' in={true} timeout={index * 300 + 300}>
      <Grid item xs={12} sm={6} xl={3}>
        <QuotationCard
          icon={
            quotationLens.manualQuotation |> get
              ? CreateOutlinedIcon
              : quotationLens.isOfferInNegotiation |> get
              ? UpdateIcon
              : quotationLens.negotiatedOffer |> get
              ? CheckIcon
              : LocalOfferIcon
          }
          iconColor={cardIconColor}
          selected={quotationSelected}
          disabled={
            (quotationLens.sourceQuotation |> get) ||
            (quotationLens.lockedYN |> get) ||
            (!(quotationLens.isActive |> get) && !(quotationLens.manualQuotation |> get))
          }
          isLastModified={isLastQuotationModified}
          title={
            <Typography color='secondary' variant='subtitle2' fontWeight='bold'>
              {quotationLens.isOfferInNegotiation |> get
                ? t('Offer.Edit.Casco.Header.OfferInNegotiation')
                : quotationLens.negotiatedOffer |> get
                ? t('Offer.Edit.Casco.Header.OfferNegotiated')
                : null}
            </Typography>
          }
        >
          <Grid>
            <QuotationTitleWrapper container alignItems='center'>
              <Grid item xs={displayLockArea ? 11 : 12}>
                <Typography variant='subtitle1' align='center'>
                  <Box fontWeight={'fontWeightBold'}>
                    <Grid container justifyContent={'center'} alignItems={'center'} sx={{ gap: '.5rem' }}>
                      {quotationLens.insurerName |> get}
                      {isLastQuotationModified && (
                        <Tooltip title={t('Offer.Edit.Casco.Header.LastQuotationModified')}>
                          <InfoOutlinedIcon fontSize='small' />
                        </Tooltip>
                      )}
                      {(quotationLens.manualQuotation |> get) === true && (
                        <Tooltip title={t('Offer.Edit.Casco.Header.ManualQuotation')}>
                          <NewReleasesIcon fontSize='small' />
                        </Tooltip>
                      )}
                    </Grid>
                  </Box>
                </Typography>
              </Grid>
              {displayLockArea && (
                <Grid item xs={1}>
                  {allowQuotationsLockUpdate ? (
                    <IconButton
                      id={`quotation_lock_${quotationLens.quotationId |> get}`}
                      size='small'
                      variant='text'
                      color='primary'
                      disabled={lockUpdating}
                      tooltip={
                        lockUpdating
                          ? ''
                          : quotationLens.lockedYN |> get
                          ? t('Offer.Edit.Casco.Header.UnlockQuotation')
                          : t('Offer.Edit.Casco.Header.LockQuotation')
                      }
                      onClick={() => onQuotationLockChange(!(quotationLens.lockedYN |> get))}
                    >
                      {lockUpdating ? <HourglassEmptyIcon /> : quotationLens.lockedYN |> get ? <LockIcon /> : <LockOpenIcon />}
                    </IconButton>
                  ) : quotationLens.lockedYN |> get ? (
                    <IconButton
                      id={`quotation_lock_${quotationLens.quotationId |> get}`}
                      tooltip={t('Offer.Edit.Casco.Header.QuotationLocked')}
                      disabled
                    >
                      <LockIcon />
                    </IconButton>
                  ) : (
                    <></>
                  )}
                </Grid>
              )}
            </QuotationTitleWrapper>
            <QuotationHeaderComponent
              quotation={quotationLens}
              validation={validation}
              index={index}
              onPropertyChange={onHeaderPropertyChange}
              onRetry={onRetry}
              onAmountChange={onAmountChange}
              disabled={disabled}
              allowEditForNegotiation={allowEditForNegotiation}
              allowRetry={allowRetry}
              titleRef={titleRef}
              showTitleTooltip={showTitleTooltip}
            />
            <RisksComponent
              risks={quotationLens.risks |> sequence}
              onPropertyChange={onRiskSelectionChange}
              colorTheme={colorTheme}
              disabled={disabled('Comparator.EditQuotation.Risks')}
              cardIconColor={cardIconColor}
            />
            <CoefficientsComponent
              quotation={quotationLens}
              colorTheme={colorTheme}
              onCoefficientsSelectionChange={onCoefficientsSelectionChange}
              disabled={disabled('Comparator.EditQuotation.Coeficients')}
              allowEmptySelection={allowEmptySelection}
              validation={validation}
              cardIconColor={cardIconColor}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {quotationLens.manualQuotation |> get ? (
                  <Button
                    fullWidth
                    id={'Edit_Convention_' + index}
                    onClick={() => onEditCustomQuotation(quotationLens.quotationId |> get)}
                    disabled={!allowCustomQuotationEdit}
                    color='primary'
                  >
                    {t('Tooltips.Edit')}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    id={'Save_Convention_' + index}
                    onClick={onSave}
                    color='primary'
                    disabled={!isDirty || (disabled('Comparator.EditQuotation') && !allowEditForNegotiation('Comparator.EditQuotation'))}
                  >
                    {saving ? t('General.Saving') : t('General.Save')}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <QuotationButton
                  fullWidth
                  id={'Select_Convention_' + index}
                  onClick={() => onSelect(quotationLens)}
                  disabled={!allowSelection}
                  endIcon={quotationSelected && <CheckCircleIcon />}
                  quotationSelected={quotationSelected}
                >
                  {quotationSelected ? t('Offer.Edit.Casco.Header.SelectedQuotation') : t('General.Select')}
                </QuotationButton>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Tooltip title={showMessageTooltip ? quotationLens.freeTxt |> get : ''} placement={'top'}>
                <span>
                  <Typography variant='caption'>
                    <Box ref={messageRef} fontStyle='italic' mt={1}>
                      {quotationLens.freeTxt |> get}
                    </Box>
                  </Typography>
                </span>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'>
                <Box mt={1}>
                  {t('Offer.Edit.Casco.LastEdit', {
                    date: t('DATE_FORMAT', {
                      date: { value: quotationLens.quotationDate |> get, format: 'DD.MM.YYYY HH:mm:ss' }
                    })
                  })}
                </Box>
              </Typography>
            </Grid>
            {!(quotationLens.manualQuotation |> get) && !(quotationLens.isActive |> get) && (
              <Grid item container xs={12}>
                <Chip label={t('Offer.Edit.Casco.UnavailableOffer')} />
              </Grid>
            )}
          </Grid>
        </QuotationCard>
      </Grid>
    </Slide>
  );
};

QuotationGridComponent.propTypes = {
  quotationLens: PropTypes.object,
  validation: PropTypes.object,
  index: PropTypes.number,
  colorTheme: PropTypes.string,
  onSave: PropTypes.func,
  onRetry: PropTypes.func,
  onSelect: PropTypes.func,
  onQuotationLockChange: PropTypes.func,
  onRiskSelectionChange: PropTypes.func,
  onHeaderPropertyChange: PropTypes.func,
  onCoefficientsSelectionChange: PropTypes.func,
  onDeleteCoefficientSelection: PropTypes.func,
  onAmountChange: PropTypes.func,
  isDirty: PropTypes.bool,
  saving: PropTypes.bool,
  lockUpdating: PropTypes.bool,
  quotationSelected: PropTypes.bool,
  disabled: PropTypes.func,
  allowEditForNegotiation: PropTypes.func,
  allowSelection: PropTypes.bool,
  allowCustomQuotationEdit: PropTypes.bool,
  allowQuotationsLockUpdate: PropTypes.bool,
  allowRetry: PropTypes.bool,
  titleRef: PropTypes.func,
  showTitleTooltip: PropTypes.bool,
  messageRef: PropTypes.func,
  showMessageTooltip: PropTypes.bool,
  onEditCustomQuotation: PropTypes.func,
  allowEmptySelection: PropTypes.bool,
  isLastQuotationModified: PropTypes.bool
};

export default QuotationGridComponent;
